import schemas from '../schemas';

import {
  Attribute,
  attr,
  fk,
  many,
  Model as BaseModel,
  oneToOne,
} from 'redux-orm';
import Ajv from 'ajv';
import {
  find,
  findKey,
  flatMap,
  get,
  identity,
  isNil,
  mapValues,
  omit,
  pick,
} from 'lodash';
//import assert from 'assert';

const ajv = new Ajv();

ajv.addSchema(Object.values(schemas));

const normalizeProp = prop =>
  prop instanceof Array
    ? prop.map(normalizeProp)
    : prop instanceof Model
      ? prop.primaryKey
      : prop;

const normalizeProps = props => mapValues(props, normalizeProp);

class Model extends BaseModel {
  static validate(props, options = {}) {
    const { partial = false } = options;

    let valid = true;
    let errors = [];
    if (partial) {
      for (const [propName, prop] of Object.entries(props)) {
        const $ref = `${this.modelName}#/properties/${propName}`;
        if (!ajv.validate({ $ref }, prop)) {
          valid = false;
          errors = [...errors, ...ajv.errors];
        }
      }
    } else {
      valid = ajv.validate(this.modelName, props);
      errors = ajv.errors ? [...ajv.errors] : [];
    }

    if (!valid)
      console.warn(
        `Error validating model %s (${partial ? 'update' : 'create'}):`,
        this.modelName,
        errors,
        props,
      );

    return { valid, errors };
  }

  static create(props, options = {}) {
    const { parse = false } = options;

    if (process.env.NODE_ENV !== 'production') {
      const primaryKey = this.pickPrimaryKey(props);
      const models = this.filter(primaryKey);
      if (models.length)
        console.error(
          'Found models with duplicate primary keys: ',
          models.map(model => model.serialize()),
        );
    }

    if (parse) props = this.parse(props);

    const normalizedProps = normalizeProps(props);
    this.validate(normalizedProps);

    return super.create(props);
  }

  static upsert(props, options) {
    // TODO: Purpose generic parse + normalize for picking the key?
    const primaryKey = this.pickPrimaryKey(props);
    const model = this.get(primaryKey);

    if (model) model.update(omit(props, this.primaryKeyFields), options);
    else this.create(props, options);
  }

  update(props, options = {}) {
    const { parse = false } = options;
    if (parse) props = this.constructor.parse(props);

    const normalizedProps = normalizeProps(props);
    this.constructor.validate(normalizedProps, { partial: true });

    return super.update(props);
  }

  toString = () => `<Model(${this.constructor.modelName}:${this.id})>`;
}

const createPropParser = (parseString, parseOther = identity) => propValue =>
  isNil(propValue)
    ? undefined
    : typeof propValue === 'string'
      ? propValue.length
        ? parseString(propValue)
        : undefined
      : parseOther(propValue);

const propParsers = {
  boolean: createPropParser(value => value === 'true'),
  integer: createPropParser(value => Number.parseInt(value, 10)),
  number: createPropParser(value => Number.parseFloat(value)),
  string: createPropParser(identity, value => String(value)),
};

const createRefParser = modelName => (propValue, session) => {
  const { [modelName]: Model } = session;
  return Model.withPrimaryKey(propValue);
};

const createRefArrayParser = modelName => (propValue, session) => {
  const { [modelName]: Model } = session;
  return propValue.map(value => Model.withPrimaryKey(value));
};

const createRefSerializer = modelName => (propValue, session) =>
  get(propValue, 'primaryKey');

const createRefArraySerializer = modelName => (propValue, session) =>
  propValue.toModelArray().map(model => get(model, 'primaryKey'));

const models = flatMap(schemas, schema => {
  const relationModels = [];
  const fields = {};
  const parsers = {};
  const serializers = {};

  for (const [fieldName, field] of Object.entries(schema.properties)) {
    const { type, items = {} } = field;
    let $ref;
    if (($ref = get(field, 'anyOf[0].$ref'))) {
      const foreignSchema = find(schemas, { $id: $ref });
      const relatedName = findKey(foreignSchema.relations, { to: schema.$id });
      const relation = get(foreignSchema.relations, relatedName, {});
      const fieldFunc = relation.type === 'one-to-one' ? oneToOne : fk;

      fields[fieldName] = fieldFunc($ref, relatedName);
      parsers[fieldName] = createRefParser($ref);
      serializers[fieldName] = createRefSerializer($ref);
    } else if (type === 'array' && ($ref = get(items, 'anyOf[0].$ref'))) {
      const foreignSchema = find(schemas, { $id: $ref });
      const relatedName = findKey(foreignSchema.relations, { to: schema.$id });
      const relation = get(foreignSchema.relations, relatedName, {});

      const fieldFunc = get(relation, 'type', '').startsWith('one-')
        ? oneToOne
        : fk;
      const foreignFieldFunc = get(relation, 'type', '').endsWith('-one')
        ? oneToOne
        : fk;

      const relationModelName = `${schema.$id}_${$ref}`;
      relationModels.push(
        class extends BaseModel {
          static modelName = relationModelName;
          static fields = {
            id: attr(),
            [schema.$id]: fieldFunc(schema.$id),
            [$ref]: foreignFieldFunc($ref),
          };
        },
      );

      fields[fieldName] = many({
        to: $ref,
        through: relationModelName,
        throughFields: [schema.$id, $ref],
        relatedName,
      });

      parsers[fieldName] = createRefArrayParser($ref);
      serializers[fieldName] = createRefArraySerializer($ref);
    } else {
      fields[fieldName] = attr();
      if (type in propParsers) parsers[fieldName] = propParsers[type];
    }
  }

  for (const [fieldName, relation] of Object.entries(schema.relations || {})) {
    parsers[fieldName] = relation.type.endsWith('-one')
      ? createRefParser(relation.to)
      : createRefArrayParser(relation.to);
  }

  return [
    class extends Model {
      static modelName = schema.$id;
      static primaryKeyFields = schema.primaryKey || ['uuid'];
      static fields = {
        id: attr(),
        ...fields,
      };

      static primaryKeyValue(value, field) {
        if (field instanceof Attribute) return value;

        const ForeignModel = this.session[field.toModelName];
        const model = ForeignModel.withPrimaryKey(value);
        return get(model, 'id');
      }

      static primaryKeyField(value, fieldName) {
        const field = fields[fieldName];

        return { [fieldName]: this.primaryKeyValue(value, field) };
      }

      static parsePrimaryKey(value) {
        const { primaryKeyFields } = this;

        if (primaryKeyFields.length === 1) {
          const [fieldName] = primaryKeyFields;
          return this.primaryKeyField(value, fieldName);
        }

        return primaryKeyFields.reduce(
          (hash, fieldName, index) => ({
            ...hash,
            ...this.primaryKeyField(get(value, index), fieldName),
          }),
          {},
        );
      }

      static pickPrimaryKey(props) {
        const { primaryKeyFields } = this;

        return mapValues(pick(props, primaryKeyFields), (value, fieldName) =>
          this.primaryKeyValue(value, fields[fieldName]),
        );
      }

      static withPrimaryKey(value) {
        return this.get(this.parsePrimaryKey(value));
      }

      get primaryKey() {
        const { primaryKeyFields } = this.constructor;

        const getKey = key =>
          get(
            this,
            fields[key] instanceof Attribute ? key : `${key}.primaryKey`,
          );

        if (primaryKeyFields.length === 1) return getKey(primaryKeyFields[0]);

        return primaryKeyFields.map(getKey);
      }

      static parse(props) {
        const { session } = this;
        return mapValues(props, (value, name) =>
          (parsers[name] || identity)(value, session),
        );
      }

      serialize() {
        const { session } = this.constructor;
        const props = pick(this, ...Object.keys(fields));
        return mapValues(props, (value, name) =>
          (serializers[name] || identity)(value, session),
        );
      }
    },
    ...relationModels,
  ];
});

export default models;



// WEBPACK FOOTER //
// ./src/orm/models.js